import { useEffect, useMemo, useState } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';

import { useSpreadsheet } from '#hooks/useSpreadsheet';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { envelopeBudget, trackingBudget } from '#spreadsheet/bindings';

import type { PlanningBudgetCategorySummary } from './types';

function useBudgetSheetNumberValue({
  binding,
  initialValue,
  sheetName,
}: {
  binding: string;
  initialValue: number | null;
  sheetName: string;
}) {
  const spreadsheet = useSpreadsheet();
  const [value, setValue] = useState<number | null>(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [binding, initialValue, sheetName]);

  useEffect(() => {
    return spreadsheet.bind(sheetName, binding, result => {
      setValue(typeof result.value === 'number' ? result.value : null);
    });
  }, [binding, sheetName, spreadsheet]);

  return value;
}

export function useLivePlanningBudgetCategorySummary({
  categoryId,
  currentMonth,
  fallbackSummary,
}: {
  categoryId: string | null | undefined;
  currentMonth: string;
  fallbackSummary?: PlanningBudgetCategorySummary;
}) {
  const [budgetType = 'envelope'] = useSyncedPref('budgetType');
  const sheetName = monthUtils.sheetForMonth(currentMonth);
  const resolvedCategoryId = categoryId ?? 'missing-category';

  const budgetedBinding = useMemo(
    () =>
      budgetType === 'tracking'
        ? trackingBudget.catBudgeted(resolvedCategoryId)
        : envelopeBudget.catBudgeted(resolvedCategoryId),
    [budgetType, resolvedCategoryId],
  );
  const balanceBinding = useMemo(
    () =>
      budgetType === 'tracking'
        ? trackingBudget.catBalance(resolvedCategoryId)
        : envelopeBudget.catBalance(resolvedCategoryId),
    [budgetType, resolvedCategoryId],
  );

  const budgeted = useBudgetSheetNumberValue({
    binding: budgetedBinding,
    initialValue: fallbackSummary?.budgeted ?? null,
    sheetName,
  });
  const balance = useBudgetSheetNumberValue({
    binding: balanceBinding,
    initialValue: fallbackSummary?.balance ?? null,
    sheetName,
  });

  return useMemo(() => {
    if (!categoryId && !fallbackSummary) {
      return undefined;
    }

    return {
      id: fallbackSummary?.id ?? categoryId ?? '',
      groupId: fallbackSummary?.groupId ?? '',
      name: fallbackSummary?.name ?? '',
      groupName: fallbackSummary?.groupName ?? '',
      spent: fallbackSummary?.spent ?? 0,
      budgeted: budgeted ?? fallbackSummary?.budgeted ?? 0,
      balance: balance ?? fallbackSummary?.balance ?? 0,
    } satisfies PlanningBudgetCategorySummary;
  }, [balance, budgeted, categoryId, fallbackSummary]);
}
